/**
 * Centralised role / event / status / recovery-tier label maps.
 *
 * 2026-05-20 (e2e closure Batch 6 / CC-7): the audit log + dashboard + admin
 * surfaces all rendered raw slugs (`resolution.signed`, `bo.lodged`, `rm`,
 * `super_admin`, `system`, `Tier 1/2/3`, `ISSUED`/`VOID`) directly to users.
 * The fix is a single source of truth here; every callsite imports from
 * `@/lib/labels` instead of inlining its own switch / map / template.
 *
 * Findings closed by routing every consumer through these maps:
 *   F02-A-031, F02-A-032, F02-P-055, F02-P-056, F02-P-061, F02-P-067,
 *   F02-P-070, F02-P-076, F02-X-076, F02-X-087, F02-X-088, F02-X-099.
 *
 * Unknown values fall back to a sensible default (the raw value is the
 * worst-case fallback only for forensic detail; never for user-facing copy).
 */

/** Map a backend role slug → fiduciary-register label. */
export const ROLE_LABELS: Record<string, string> = {
  admin: 'Administrator',
  compliance: 'Compliance officer',
  super_admin: 'Super administrator',
  superadmin: 'Super administrator',
  rm: 'Relationship manager',
  director: 'Director',
  user: 'Member',
  trustee: 'Trustee',
  beneficiary: 'Beneficiary',
  counsel: 'External counsel',
  witness: 'Witness',
  auditor: 'External auditor',
  system: 'System',
};

/**
 * A firm's own TITLES for the four firm-staff roles (2026-08-22 Klaprops
 * meeting). Mirrors `TenantDoc.roleLabels` on the backend and arrives on
 * `GET /v1/tenants/me`.
 *
 * LABELS ONLY. Renaming a role changes what a firm's people SEE, never what the
 * role can DO — every authorization decision still reads the canonical role key
 * through `lib/authz/*`. That is also why only these four keys exist: a tenant
 * cannot rename `super_admin` (the platform operator), the portal `user`
 * (a client, not staff), or the statutory titles (MLRO, Information Officer,
 * external auditor), which are legal terms rather than firm preferences.
 */
export interface RoleLabelOverrides {
  admin?: string;
  compliance?: string;
  rm?: string;
  director?: string;
}

/** The four role keys a firm may retitle, with their canonical Elreign names.
 *  An OBJECT, not an array of role keys — the backend's `lint-role-sets` guard
 *  flags role arrays for good reason, and the same discipline keeps this from
 *  ever being mistaken for an authorization set. */
export const RENAMEABLE_ROLES: Record<keyof RoleLabelOverrides, string> = {
  admin: ROLE_LABELS['admin']!,
  compliance: ROLE_LABELS['compliance']!,
  rm: ROLE_LABELS['rm']!,
  director: ROLE_LABELS['director']!,
};

/**
 * Resolve a role slug to its label, with graceful fallback.
 *
 * `overrides` is the firm's own naming (see {@link RoleLabelOverrides}); an
 * absent, empty or whitespace-only entry falls through to the canonical Elreign
 * label, so a half-configured firm never renders a blank role.
 */
export function labelForRole(
  role: string | undefined | null,
  overrides?: RoleLabelOverrides | null,
): string {
  if (!role) return 'User';
  const key = role.toLowerCase();
  // The renameable set is checked at RUNTIME, not just in the type. The
  // backend's `.strict()` schema is the real gate, but this is the last line
  // between a hand-edited tenant doc and a firm quietly relabelling the
  // platform operator (or a portal client) inside its own console.
  if (Object.prototype.hasOwnProperty.call(RENAMEABLE_ROLES, key)) {
    const custom = overrides?.[key as keyof RoleLabelOverrides];
    if (typeof custom === 'string' && custom.trim().length > 0) return custom.trim();
  }
  return ROLE_LABELS[key] ?? capitaliseFirst(role);
}

/**
 * The shape {@link resolveRoleDisplay} needs from a firm's custom roles.
 * Structurally identical to `CustomRoleView` in `lib/api/branding`, restated
 * here so this module — which every surface imports for LABELS — does not take
 * a dependency on the API layer.
 */
export interface CustomRoleLike {
  id: string;
  label: string;
}

/**
 * What word to show for one member, given everything the firm has configured.
 *
 * THREE SOURCES, IN ORDER, AND THE ORDER IS THE POINT:
 *
 *   1. `roleLabelId` — the firm invented a chair ("Trust Officer") and put this
 *      person in it. Most specific: it is the only one of the three that can
 *      distinguish two people holding the SAME canonical role.
 *   2. the firm's title for that canonical role ("Client partner" for `rm`).
 *   3. the canonical Elreign name ("Relationship manager").
 *
 * Every step degrades rather than fails. A `roleLabelId` pointing at a custom
 * role the firm has since deleted falls to (2), and a firm that has renamed
 * nothing falls to (3) — so a directory row can never render blank, which is
 * exactly what a lookup-or-nothing resolver would do the first time an admin
 * tidied up their role list.
 *
 * NOT AN AUTHORIZATION FUNCTION. It returns a string for a human to read.
 * `role` is the canonical token and stays the only thing any gate consults.
 */
export function resolveRoleDisplay(
  role: string | undefined | null,
  opts?: {
    roleLabelId?: string | undefined;
    customRoles?: readonly CustomRoleLike[] | null;
    overrides?: RoleLabelOverrides | null;
  },
): string {
  const id = opts?.roleLabelId;
  if (id) {
    const hit = opts?.customRoles?.find((r) => r.id === id);
    if (hit && hit.label.trim().length > 0) return hit.label.trim();
  }
  return labelForRole(role, opts?.overrides ?? null);
}

/**
 * Audit event labels live in `event-labels.ts`: one map covering every action
 * the backend records, with a neutral line (never the code) for the rest.
 */
export {
  EVENT_LABELS,
  EVENT_PATTERNS,
  EVENT_SUBJECT_LABELS,
  UNKNOWN_EVENT_LABEL,
  labelForEvent,
  labelForEventSubject,
} from './event-labels';

/** Status enums rendered to users. */
export const STATUS_LABELS: Record<string, string> = {
  // Tax certificates
  ISSUED: 'Issued',
  AMENDED: 'Amended',
  VOID: 'Voided',
  PENDING: 'Pending',
  // Resolution statuses
  draft: 'Draft',
  awaiting: 'Awaiting signatures',
  completed: 'Completed',
  cancelled: 'Cancelled',
  // FICA report
  'received': 'Received',
  'approved': 'Approved',
  'rejected': 'Rejected',
  // DSAR
  RECEIVED: 'Received',
  ACKNOWLEDGED: 'Acknowledged',
  FULFILLED: 'Fulfilled',
  DECLINED: 'Declined',
};

export function labelForStatus(status: string | undefined | null): string {
  if (!status) return 'Not recorded';
  return STATUS_LABELS[status] ?? capitaliseFirst(status);
}

/**
 * Recovery tier labels — used in `/auth/recovery` + mobile account-recovery.
 *
 * 2026-05-20 (F02-A-031, F02-A-032, F02-N-153): "Tier 1/2/3" was internal
 * taxonomy showing up on customer-facing screens. The selector itself
 * already describes each tier in plain English; the CTAs/badges should
 * use action language instead of the tier numbers.
 */
export const RECOVERY_TIER_LABELS: Record<string, string> = {
  '1': 'Submit recovery code',
  '2': 'Reset two-factor',
  '3': 'Verify my identity',
  'tier-1': 'Submit recovery code',
  'tier-2': 'Reset two-factor',
  'tier-3': 'Verify my identity',
};

export function labelForRecoveryTier(tier: string | number | undefined | null): string {
  if (tier === undefined || tier === null || tier === '') return 'Begin recovery';
  const key = String(tier).toLowerCase();
  return RECOVERY_TIER_LABELS[key] ?? 'Begin recovery';
}

function capitaliseFirst(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * ── DEED VOCABULARY ─────────────────────────────────────────────────────────
 *
 * The deed's stored words, rendered in the words a client reads.
 *
 * RULING (Beno, 2026-08-22, re-affirmed by Paul four separate times across the
 * 22, 23 and 25 August walkthroughs): the person who founds a trust is the
 * **Founder**, platform-wide. "Settlor" is the term the older deeds use and the
 * term the extraction pipeline stores verbatim, and it is the term a South
 * African fiduciary practice does not put in front of a client.
 *
 * WHY THIS FUNCTION EXISTS RATHER THAN A RENAME. The roster role is FREE TEXT
 * lifted off the deed ("Settlor · Trustee", "Skenker/Donor-Founder"). Rewriting
 * the stored value would put words in a legal instrument's mouth, which is the
 * exact rule this product is built on — a fact not in the deed must not show.
 * So the stored text stays as the deed wrote it and this maps it at the point
 * of display.
 *
 * WHY IT IS *HERE* AND NOT INLINE. It was inline. `specificRosterRole` in
 * people-screen.tsx carried its own `.replace(/settlor/gi, 'Founder')` and the
 * badge beside it read "Founder · Trustee" correctly — while the grey subtitle
 * two lines above rendered `m.rosterRole` raw and read "Settlor · Trustee".
 * One screen, one fact, two render paths, one of them mapped. That is why the
 * word survived three "fixed" waves and was still on the deployed build on
 * 25 August. Every path now goes through this one function, and
 * `scripts/lint-deed-vocabulary.mjs` fails the build if a new one does not.
 *
 * Not mapped on purpose: `donor` and `skenker` reach us only as part of a
 * compound capacity ("Skenker/Donor-Founder") that already carries the word
 * Founder, and a bare "Donor" is a distinct fiduciary capacity that is not
 * ours to rename.
 */
const DEED_VOCABULARY: ReadonlyArray<readonly [RegExp, string]> = [
  // Plurals first — a bare /settlor/ would leave a stray "s" behind.
  [/\bsettlors\b/g, 'Founders'],
  [/\bSettlors\b/g, 'Founders'],
  [/\bSETTLORS\b/g, 'FOUNDERS'],
  [/\bsettlor\b/g, 'Founder'],
  [/\bSettlor\b/g, 'Founder'],
  [/\bSETTLOR\b/g, 'FOUNDER'],
  // The misspelling is not hypothetical: it is in stored deed text and in
  // every Gemini transcript of these meetings.
  [/\bsettlers\b/g, 'Founders'],
  [/\bSettlers\b/g, 'Founders'],
  [/\bsettler\b/g, 'Founder'],
  [/\bSettler\b/g, 'Founder'],
];

/**
 * Map free-text deed capacity → the label a client reads.
 *
 * Pass ANY roster/deed role string through this before it reaches the DOM,
 * including compound capacities ("Settlor · Trustee" → "Founder · Trustee").
 * Null, undefined and empty pass straight through so callers keep their own
 * fallback copy.
 */
export function deedRoleLabel<T extends string | null | undefined>(raw: T): T {
  if (raw === null || raw === undefined) return raw;
  const s = String(raw);
  if (s.length === 0) return raw;
  let out = s;
  for (const [pattern, replacement] of DEED_VOCABULARY) {
    out = out.replace(pattern, replacement);
  }
  return out as T;
}

/**
 * A beneficiary's relationship as a line under their name, or '' when there
 * is nothing to add to the word "Beneficiary".
 *
 * When ingestion reads no relationship off the deed, the backend stores the
 * placeholder "Beneficiary (from document)" as the relationship
 * (`confirm-core.ts`, `commit-manifest.ts`). Printed beside the role it read
 * "Beneficiary · Beneficiary (from document)" (NEW-R12-BENEFICIARY-DOUBLE-
 * LABEL, grader round 12). A relationship that only restates the role says
 * nothing, so it is dropped; a real one ("Daughter") is kept.
 */
export function beneficiaryRelationshipLabel(raw: string | null | undefined): string {
  const s = (raw ?? '').trim();
  if (!s || /^beneficiar(y|ies)\b/i.test(s)) return '';
  return deedRoleLabel(s);
}
